import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingErrorsAtom,
  highlightedAfterEditAtomFamily,
  normalizeRideEntity,
  type RydeUiUnit,
} from "@ryde/shared";

import { useGlobalEffectActions } from "../use-global-effect-actions";
import {
  shouldResetPageOnClearAll,
  shouldResetPageOnFiltersChange,
  shouldResetPageOnSearchChange,
} from "./dynamic-tables-cleanup.utils";

export const useDynamicTablesCleanup = ({ entity }: { entity: RydeUiUnit }) => {
  /* normalized entity ensures compatibility with ride edit form,
  to avoid highlighting rows by wrong ids if the form is opened not from ride page*/
  const entityNormalized = normalizeRideEntity(entity);
  const setApiServerErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const { resetPaginationAppEvent } = useGlobalEffectActions();
  const setHighlightedRows = useSetRecoilState(
    highlightedAfterEditAtomFamily(entityNormalized),
  );

  const prepareServerSideTableFetch = useEvent(
    ({ skipPageReset }: { skipPageReset: boolean | undefined }) => {
      if (!skipPageReset) {
        resetPaginationAppEvent();
      }

      setHighlightedRows([]);
      setApiServerErrors(undefined);
    },
  );

  const prepareClientSideTableFetch = useEvent(() => {
    resetPaginationAppEvent();
    setHighlightedRows([]);
    setApiServerErrors(undefined);
  });

  const resetPageOnFiltersChange = useEvent(() => {
    if (shouldResetPageOnFiltersChange(entity)) {
      resetPaginationAppEvent();
    }
  });

  const resetPageOnSearchChange = useEvent(() => {
    if (shouldResetPageOnSearchChange(entity)) {
      resetPaginationAppEvent();
    }
  });

  const resetPageOnClearAll = useEvent(() => {
    if (shouldResetPageOnClearAll(entity)) {
      resetPaginationAppEvent();
    }
  });

  return {
    prepareServerSideTableFetch,
    prepareClientSideTableFetch,
    resetPageOnFiltersChange,
    resetPageOnSearchChange,
    resetPageOnClearAll,
  };
};
